// BLOBL motion engine: physically based springs, like iOS/SwiftUI.
//
// A spring pulls a value (or a list of values) towards a target. It has no fixed duration: it
// settles when it is at rest, and changing the target mid-flight keeps the current velocity,
// so interrupted animations never jump.
//
// Configs use the SwiftUI parameters:
//   response - seconds for one oscillation (lower = faster, snappier)
//   damping  - damping fraction: 1 = no overshoot, < 1 = bouncy, > 1 = sluggish
//
// All running springs share one requestAnimationFrame loop. Each frame first steps every spring,
// then calls every onUpdate, so an onUpdate may read the current value of another spring.

export const springs = {
  // Default for moving things around (iOS default spring)
  smooth: { response: 0.5, damping: 0.86 },
  // Quick UI feedback: menus, small elements
  snappy: { response: 0.32, damping: 0.8 },
  // Playful pop with a little overshoot
  bouncy: { response: 0.42, damping: 0.68 },
  // Sent message flying from the input into the chat
  send: { response: 0.48, damping: 0.82 },
  // Things closing: quick and without bounce, like iOS
  exit: { response: 0.28, damping: 1 },
  // Pages pushed in from the side (iOS navigation)
  page: { response: 0.42, damping: 0.95 },
  // Sheets and dialogs coming up
  sheet: { response: 0.4, damping: 0.86 },
  // Height of sections opening and closing (overshooting heights look wrong)
  collapse: { response: 0.38, damping: 1 },
};

const STEP = 1 / 240; // fixed physics step for stable results at any frame rate
const MAX_FRAME = 1 / 20; // after a stalled tab, don't simulate a huge jump

const running = new Set();
let frame = null;
let lastTime = 0;

function tick(now) {
  const dt = Math.min((now - lastTime) / 1000, MAX_FRAME);
  lastTime = now;
  for (const s of running) s.step(dt);
  for (const s of running) {
    s.render();
    if (s.atRest) {
      running.delete(s);
      s.complete();
    }
  }
  frame = running.size ? requestAnimationFrame(tick) : null;
}

function schedule(s) {
  running.add(s);
  if (!frame) {
    lastTime = performance.now();
    frame = requestAnimationFrame(tick);
  }
}

let reducedQuery = null;
// Users who ask the OS for less motion get the end state right away
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  reducedQuery = reducedQuery || window.matchMedia('(prefers-reduced-motion: reduce)');
  return reducedQuery.matches;
}

const toArray = (v) => (Array.isArray(v) ? [...v] : [v]);

class Spring {
  constructor({ from, to, velocity = 0, config = springs.smooth, precision = 0.01, onUpdate, onComplete }) {
    this.vector = Array.isArray(from);
    this.x = toArray(from);
    this.target = toArray(to);
    this.v = Array.isArray(velocity) ? [...velocity] : this.x.map(() => velocity);
    this.precision = precision;
    this.onUpdate = onUpdate;
    this.onComplete = onComplete;
    this.atRest = false;
    this.stopped = false;
    this.setConfig(config);
  }

  setConfig({ response = 0.5, damping = 0.86 }) {
    const omega = (2 * Math.PI) / response;
    this.k = omega * omega; // stiffness (mass 1)
    this.c = 2 * damping * omega; // damping coefficient
  }

  step(dt) {
    let left = dt;
    while (left > 1e-6) {
      const h = Math.min(STEP, left);
      left -= h;
      for (let i = 0; i < this.x.length; i++) {
        const a = -this.k * (this.x[i] - this.target[i]) - this.c * this.v[i];
        this.v[i] += a * h; // semi-implicit Euler: velocity first
        this.x[i] += this.v[i] * h;
      }
    }
    // At rest when every component is close to its target and nearly still
    const p = this.precision;
    this.atRest = this.x.every((x, i) => Math.abs(x - this.target[i]) < p && Math.abs(this.v[i]) < p * 10);
    if (this.atRest) {
      this.x = [...this.target];
      this.v = this.v.map(() => 0);
    }
  }

  render() {
    this.onUpdate?.(this.vector ? this.x : this.x[0]);
  }

  complete() {
    this.onComplete?.();
  }
}

// Starts a spring. Returns a controller:
//   value / velocity  current state (number, or array for array input)
//   set(to, config?)  new target, keeps the velocity
//   stop()            freezes where it is, no onComplete
//   finish()          jumps to the target and completes
export function spring(options) {
  const s = new Spring(options);
  const read = (arr) => (s.vector ? [...arr] : arr[0]);

  const controller = {
    get value() { return read(s.x); },
    get velocity() { return read(s.v); },
    get running() { return running.has(s); },
    set(to, config) {
      if (s.stopped) return controller;
      s.target = toArray(to);
      if (config) s.setConfig(config);
      s.atRest = false;
      schedule(s);
      return controller;
    },
    stop() {
      s.stopped = true;
      running.delete(s);
      return controller;
    },
    finish() {
      if (s.stopped) return controller;
      s.stopped = true;
      running.delete(s);
      s.x = [...s.target];
      s.v = s.v.map(() => 0);
      s.render();
      s.complete();
      return controller;
    },
  };

  if (prefersReducedMotion()) {
    // Render the end state in the next frame so callers can finish their setup first
    requestAnimationFrame(() => controller.finish());
  } else {
    s.render();
    schedule(s);
  }
  return controller;
}
